import {
  ref,
  set,
  get,
  update,
  remove,
  onValue,
  push,
  onDisconnect,
} from 'firebase/database';
import { database } from './firebaseConfig';
import { IRoomRepository } from '../../app/ports/IRoomRepository';
import { Room, Participant, VotingRound, ThrowEvent, ReactionEvent, DeckType } from '../../domain/entities/Room';

function materialiseRoom(raw: any): Room {
  const room = raw as Room;
  // Back-compat: rooms created before deckType existed default to fibonacci.
  if (!room.deckType) room.deckType = 'fibonacci';
  return room;
}

export class FirebaseRoomRepository implements IRoomRepository {
  async createRoom(name: string, adminId: string, adminName: string, adminAnimal: string): Promise<string> {
    const roomRef = push(ref(database, 'rooms'));
    const roomId = roomRef.key!;
    const participant: Participant = {
      userId: adminId,
      name: adminName,
      animal: adminAnimal,
      vote: null,
      isAdmin: true,
      isConnected: true,
      joinedAt: Date.now(),
    };
    await set(roomRef, {
      id: roomId,
      name,
      adminId,
      status: 'waiting',
      participants: { [adminId]: participant },
      currentStory: '',
      createdAt: Date.now(),
      votingHistory: [],
      deckType: 'fibonacci',
    });
    return roomId;
  }

  async getRoom(roomId: string): Promise<Room | null> {
    const snap = await get(ref(database, `rooms/${roomId}`));
    if (!snap.exists()) return null;
    return materialiseRoom(snap.val());
  }

  subscribeToRoom(roomId: string, callback: (room: Room | null) => void): () => void {
    const roomRef = ref(database, `rooms/${roomId}`);
    const unsubscribe = onValue(roomRef, (snap) => {
      callback(snap.exists() ? materialiseRoom(snap.val()) : null);
    });
    return unsubscribe;
  }

  async joinRoom(roomId: string, participant: Participant): Promise<void> {
    await update(ref(database, `rooms/${roomId}/participants/${participant.userId}`), participant);
    const connRef = ref(database, `rooms/${roomId}/participants/${participant.userId}/isConnected`);
    await set(connRef, true);
    onDisconnect(connRef).set(false);
  }

  async leaveRoom(roomId: string, userId: string): Promise<void> {
    await remove(ref(database, `rooms/${roomId}/participants/${userId}`));
  }

  async submitVote(roomId: string, userId: string, vote: string): Promise<void> {
    await update(ref(database, `rooms/${roomId}/participants/${userId}`), { vote });
  }

  async revealVotes(roomId: string): Promise<void> {
    await update(ref(database, `rooms/${roomId}`), { status: 'revealed' });
  }

  async resetVoting(roomId: string, newStory?: string): Promise<void> {
    const room = await this.getRoom(roomId);
    if (!room) return;

    const updates: Record<string, any> = { status: 'waiting' };
    if (newStory !== undefined) updates.currentStory = newStory;

    Object.keys(room.participants || {}).forEach((uid) => {
      updates[`participants/${uid}/vote`] = null;
    });

    await update(ref(database, `rooms/${roomId}`), updates);
  }

  async setCurrentStory(roomId: string, story: string): Promise<void> {
    // Rename only — never flip status. Reveal/Reset own status transitions.
    await update(ref(database, `rooms/${roomId}`), { currentStory: story });
  }

  async setDeckType(roomId: string, deckType: DeckType): Promise<void> {
    const room = await this.getRoom(roomId);
    if (!room) return;

    const updates: Record<string, any> = {
      deckType,
      status: 'waiting',
    };
    Object.keys(room.participants || {}).forEach((uid) => {
      updates[`participants/${uid}/vote`] = null;
    });
    await update(ref(database, `rooms/${roomId}`), updates);
  }

  async saveVotingRound(roomId: string, round: VotingRound): Promise<void> {
    const histRef = push(ref(database, `rooms/${roomId}/votingHistory`));
    await set(histRef, { ...round, deckType: round.deckType || 'fibonacci' });
  }

  async setParticipantConnection(roomId: string, userId: string, connected: boolean): Promise<void> {
    await update(ref(database, `rooms/${roomId}/participants/${userId}`), { isConnected: connected });
  }

  async deleteRoom(roomId: string): Promise<void> {
    await remove(ref(database, `rooms/${roomId}`));
  }

  async sendThrow(roomId: string, event: ThrowEvent): Promise<void> {
    const eventRef = ref(database, `rooms/${roomId}/throws/${event.id}`);
    await set(eventRef, event);
    setTimeout(() => remove(eventRef).catch(() => {}), 5000);
  }

  async sendReaction(roomId: string, event: ReactionEvent): Promise<void> {
    const eventRef = ref(database, `rooms/${roomId}/reactions/${event.id}`);
    await set(eventRef, event);
    setTimeout(() => remove(eventRef).catch(() => {}), 5000);
  }
}
